// Libs
import { clone, uniq } from "lodash";
import { useHistory } from "react-router";

// Types
import { MarketingActionType } from "src/types/enum/MarketingActionType";
import { UserRoleEnum } from "src/types/role";
import { MarketingActionVariantEnum } from "src/utils/variants";
// Utils
import { COMPONENTS_TRANSLATION_NAMESPACE } from "src/components/config";
import { useTranslation } from "src/design-system/core/TranslationContext";
import useAuth from "src/hooks/useAuth";
import { displaySmartScheduler } from "src/utils/ABTesting/displaySmartScheduler";
import { isComboDisabled, isSpecialOfferDisabled } from "src/views-ds/offer/utils/utils";

// Components
import { MarketingActionVariantIcons } from "src/components-ds/displays";
import { Button, ButtonContainer } from "src/design-system/molecules/buttons";
import { type HorizontalTransform, Tooltip } from "src/design-system/molecules/modals";
import { ButtonDropdown } from "src/design-system/organisms/buttons";

// TODO : OLD VIEWS IMPORTS
import { track } from "@amplitude/analytics-browser";
import { convertCamelCaseToKebabCase } from "src/utils/caseConverter";
import {
    MARKETING_ACTIONS_CREATE_VIEW_TK,
    MARKETING_ACTION_SELECTOR_TRANSLATION_NAMESPACE,
} from "src/views/marketing-actions/components/MarketingActionSelector/config";
import { marketingActionRoutes } from "src/views/marketing-actions/routes";

export const CREATE_OFFER_BUTTON_TK = "CREATE_OFFER_BUTTON_TK";

export function CreateOfferButton({
    hideShortcutAction = false,
    groupedMoreButtonProps,
    dropdownHorizontalTransform,
    page,
}: {
    hideShortcutAction?: boolean;
    groupedMoreButtonProps?: React.ComponentProps<typeof ButtonDropdown>["moreButtonProps"];
    dropdownHorizontalTransform?: HorizontalTransform;
    page?: string;
}): JSX.Element {
    const { tComponent, tMarketingAction, translateCommon } = useTranslation({
        component: `${COMPONENTS_TRANSLATION_NAMESPACE}:${CREATE_OFFER_BUTTON_TK}`,
        marketingAction: `${MARKETING_ACTION_SELECTOR_TRANSLATION_NAMESPACE}:${MARKETING_ACTIONS_CREATE_VIEW_TK}`,
    });
    const history = useHistory();
    const { user } = useAuth();

    const disabled = user.channels.length === 0 || user.role === UserRoleEnum.SUPERADMIN;
    const isDisplaySmart = displaySmartScheduler(user?.organization?.id?.toString() ?? "");

    // SS BUTTON
    const tooltipSSButton = tComponent("connect-externa-channel", {
        concept: translateCommon("concepts.an-auto-rule"),
    });
    const labelSSButton = translateCommon(`actions.create-concept`, {
        concept: translateCommon("concepts.a-smart-scheduler"),
    });
    const handleClickSSButton = () =>
        history.push(
            marketingActionRoutes.offer({
                variants: [
                    MarketingActionVariantEnum.SmartScheduler,
                    MarketingActionVariantEnum.Offer,
                ],
            }),
            {
                previousLocation: clone(history.location),
            }
        );

    // GROUPED BUTTON
    const tooltipGroupedButton = tComponent("connect-externa-channel", {
        concept: translateCommon("concepts.a-offer"),
    });
    const labelGroupedButton = translateCommon(`actions.create-concept`, {
        concept: translateCommon("concepts.a-offer"),
    });

    const handleClickGroupedButtonItem = (variant: MarketingActionVariantEnum) => {
        track("click-button", {
            page: page ?? "unknown-page",
            name: `create-offer`,
            actionType: convertCamelCaseToKebabCase(variant),
            MarketingActionType: MarketingActionType.Offer,
        });
        history.push(
            marketingActionRoutes.offer({
                variants: uniq([MarketingActionVariantEnum.Offer, variant]),
            }),
            { previousLocation: clone(history.location) }
        );
    };

    const serviceList = user?.channels?.map((e) => e.source) ?? [];
    const disabledCombo = isComboDisabled(serviceList);
    const disabledSpecial = isSpecialOfferDisabled(serviceList);

    const ListVariantOrder = [
        undefined,
        MarketingActionVariantEnum.Offer,
        MarketingActionVariantEnum.AutoRule,
        MarketingActionVariantEnum.SmartScheduler,
        MarketingActionVariantEnum.Special,
    ].filter((e) => {
        if (isDisplaySmart === false && e === MarketingActionVariantEnum.SmartScheduler) {
            return false;
        }
        if (disabledCombo === true && e === MarketingActionVariantEnum.Duo) {
            return false;
        }
        if (disabledSpecial === true && e === MarketingActionVariantEnum.Special) {
            return false;
        }
        return true;
    });

    const groupedItemList = ListVariantOrder.map((variant) => {
        return {
            key: variant ?? "title-grouped-items-offer",
            icon: variant ? (
                <MarketingActionVariantIcons
                    variants={
                        variant === MarketingActionVariantEnum.Offer
                            ? [variant, MarketingActionVariantEnum.Classic]
                            : [variant]
                    }
                />
            ) : undefined,
            isGroupTitle: variant === undefined,
            label: variant
                ? translateCommon(`concepts.${variant}`)
                : tMarketingAction(`title`, {
                      concept: translateCommon(`concepts.${MarketingActionVariantEnum.Offer}`),
                  }),
            secondaryLabel: variant
                ? tMarketingAction(`${MarketingActionType.Offer}.item.${variant}.subtitle`)
                : "",
            action: () => (variant ? handleClickGroupedButtonItem(variant) : undefined),
            className: variant ? "fl-c-create-offer-button__item" : "",
        };
    });

    return (
        <ButtonContainer>
            {!hideShortcutAction && isDisplaySmart && (
                <Tooltip tooltip={tooltipSSButton} disabled={!disabled}>
                    <Button
                        disabled={disabled}
                        variant="outlined"
                        iconSize="s"
                        icon="offer-smart-scheduler"
                        onClick={handleClickSSButton}
                        label={labelSSButton}
                    />
                </Tooltip>
            )}

            <Tooltip tooltip={tooltipGroupedButton} disabled={!disabled}>
                <ButtonDropdown
                    label={labelGroupedButton}
                    actions={groupedItemList}
                    moreButtonProps={{
                        icon: "add-circle-outline",
                        iconPosition: "left",
                        disabled,
                        ...groupedMoreButtonProps,
                    }}
                    {...(dropdownHorizontalTransform && {
                        anchorOrigin: {
                            vertical: "bottom",
                            horizontal: dropdownHorizontalTransform,
                        },
                        transformOrigin: {
                            vertical: "top",
                            horizontal: dropdownHorizontalTransform,
                        },
                    })}
                    dropdownProps={{ contentClassName: "fl-c-create-offer-button" }}
                />
            </Tooltip>
        </ButtonContainer>
    );
}
